export type HashRouteType =
  | 'todo-detail'
  | 'subgoal-todos'
  | 'goal-detail'
  | 'new-goal'
  | 'profile'
  | 'settings'
  | 'oauth-consent'
  | 'ai-drafts'
  | 'privacy'
  | 'terms'
  | 'default';

export interface HashRouteInfo {
  type: HashRouteType;
  subgoalId?: string;
  todoId?: string;
  goalId?: string;
  /** oauth-consent: the authorization request to answer */
  requestId?: string;
}

export const OAUTH_CONSENT_PATH = '/oauth/consent';

/** True for the consent screen an MCP app sends the user to (works before sign-in too). */
export function isOAuthConsentHash(currentRoute: string): boolean {
  return parseHashRoute(currentRoute).type === 'oauth-consent';
}

/** Strip leading #; empty hash becomes '/'. */
export function getHashPath(currentRoute: string): string {
  return currentRoute.replace('#', '') || '/';
}

/**
 * Pure hash-route parser — no side effects (no window.open).
 * Legal paths return type privacy|terms; callers may redirect separately.
 */
export function parseHashRoute(currentRoute: string): HashRouteInfo {
  const hash = getHashPath(currentRoute);

  const todoDetailMatch = hash.match(/^\/subgoals\/([^/]+)\/todos\/([^/]+)$/);
  if (todoDetailMatch) {
    return {
      type: 'todo-detail',
      subgoalId: todoDetailMatch[1],
      todoId: todoDetailMatch[2],
    };
  }

  const subgoalMatch = hash.match(/^\/subgoals\/([^/]+)\/todos$/);
  if (subgoalMatch) {
    return {
      type: 'subgoal-todos',
      subgoalId: subgoalMatch[1],
    };
  }

  // Yeni hedef (T-183): #/goals/new, with ?draft=, ?goal= or ?ai=1
  if (hash === '/goals/new' || hash.startsWith('/goals/new?')) {
    return { type: 'new-goal' };
  }

  const goalDetailMatch = hash.match(/^\/goals\/([^/?]+)$/);
  if (goalDetailMatch) {
    return {
      type: 'goal-detail',
      goalId: goalDetailMatch[1],
    };
  }

  if (hash === '/profile') {
    return { type: 'profile' };
  }

  if (hash === '/settings') {
    return { type: 'settings' };
  }

  // MCP app asking to connect: #/oauth/consent?request=<id> (sent here by mcp.riselio.com/authorize)
  if (hash === OAUTH_CONSENT_PATH || hash.startsWith(`${OAUTH_CONSENT_PATH}?`)) {
    const query = new URLSearchParams(hash.slice(OAUTH_CONSENT_PATH.length + 1));
    return { type: 'oauth-consent', requestId: query.get('request') ?? undefined };
  }

  if (hash === '/ai-drafts') {
    return { type: 'ai-drafts' };
  }

  if (hash === '/privacy') {
    return { type: 'privacy' };
  }

  if (hash === '/terms') {
    return { type: 'terms' };
  }

  return { type: 'default' };
}

/** The four tabs of the Yön B shell (Faz 23): Bugün, Hedefler, Zaman, İlerleme. */
export type TabType = 'home' | 'goals' | 'time' | 'progress';

const TAB_PATHS: Record<string, TabType> = {
  '/': 'home',
  '/home': 'home',
  '/goals': 'goals',
  '/time': 'time',
  '/progress': 'progress',
  // The paths before Faz 23 still open their tab (saved links, the site, the native app).
  '/ongoing': 'time',
  '/timely': 'time',
  '/achievements': 'progress',
};

/**
 * Navigation tab that owns the given hash. Goal/subgoal/todo detail pages and
 * AI drafts belong to "goals"; pages outside the tab bar (profile, settings) return null.
 */
export function getTabForHash(currentRoute: string): TabType | null {
  // A query (#/goals?tag=…) does not change the tab
  const hash = getHashPath(currentRoute).split('?')[0];
  if (TAB_PATHS[hash]) return TAB_PATHS[hash];
  if (/^\/(goals|subgoals)\//.test(hash) || hash === '/ai-drafts') return 'goals';
  if (hash === '/profile' || hash === '/settings' || hash.startsWith(OAUTH_CONSENT_PATH)) return null;
  return 'home';
}
